/**
 * German-first LanguageSplitter helpers
 *
 * Resolves Firestore `LanguageSplitter` fields to display strings for onboarding copy,
 * including `&&&` line breaks used in content sync.
 */

import type { LanguageSplitter } from '@physiofit-digital/physiofit_types';

export type ResolveLanguageSplitterLang = 'de' | 'en';

/**
 * Returns German text from a LanguageSplitter, with English fallback inside the resolver.
 *
 * @param {Object | undefined} textObject Localized string object.
 * @return {string} Resolved German (or fallback) text.
 */
export function pickGermanText(textObject?: LanguageSplitter): string {
  return resolveLanguageSplitter(textObject, 'de');
}

/**
 * Maps an array of LanguageSplitter items to non-empty German strings.
 *
 * @param {Object[] | undefined} items List of localized objects.
 * @return {string[]} German strings with empty entries removed.
 */
export function pickGermanTextList(items?: LanguageSplitter[]): string[] {
  if (!items?.length) {
    return [];
  }
  return items.map((item) => pickGermanText(item)).filter((text) => text.length > 0);
}

function pickLocalizedString(
  textObject: LanguageSplitter,
  lang: ResolveLanguageSplitterLang,
): string {
  if (lang === 'en') {
    const en = textObject.en;
    if (en !== undefined && en !== '') {
      return String(en);
    }
    const de = textObject.de;
    return de !== undefined && de !== '' ? String(de) : '';
  }
  const de = textObject.de;
  if (de !== undefined && de !== '') {
    return String(de);
  }
  const en = textObject.en;
  return en !== undefined && en !== '' ? String(en) : '';
}

/**
 * Resolves LanguageSplitter to a single string (German-first, optional English).
 *
 * @param {Object | undefined} textObject Localized string object.
 * @param {string} lang Preferred language (`de` or `en`).
 * @return {string} Resolved text with `&&&` converted to newlines when present.
 */
export function resolveLanguageSplitter(
  textObject?: LanguageSplitter,
  lang: ResolveLanguageSplitterLang = 'de',
): string {
  if (!textObject) {
    return '';
  }
  const text = pickLocalizedString(textObject, lang);
  if (!text) {
    return '';
  }
  if (text.includes('//')) {
    return text;
  }
  return text.replace(/\s*&&&\s*/g, '\n');
}
